import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// A right-click menu. One component for every page that offers one (the Estimate table, the takeoff canvas), so the
// look, keyboard use and edge handling are the same everywhere.
//
//   menu  = { x, y, items } | null     x / y are client (viewport) coordinates, straight from the contextmenu event
//   items = [{ label, onClick, disabled?, why?, danger?, hint?, checked? } | { separator: true } | { heading: "Text" }]
//   A disabled item stays listed and greys out; `why` is its tooltip, so the menu says what is not possible and why.
//
// Esc, a click outside, scrolling or a window resize close it; ↑ ↓ Home End move, Enter runs. It is placed inside
// the viewport (flips up / left when it would run off an edge).
export function openContextMenu(ev, items, setMenu) {
  ev.preventDefault();
  ev.stopPropagation();
  setMenu({ x: ev.clientX, y: ev.clientY, items });
}

export default function ContextMenu({ menu, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: menu?.x ?? 0, top: menu?.y ?? 0 });
  const [active, setActive] = useState(-1);
  const actionable = (menu?.items || []).map((it, i) => (it.separator || it.heading || it.disabled ? -1 : i)).filter(i => i >= 0);

  useLayoutEffect(() => {
    if (!menu || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const left = Math.max(4, Math.min(menu.x, window.innerWidth - r.width - 4));
    const top = Math.max(4, Math.min(menu.y, window.innerHeight - r.height - 4));
    setPos({ left, top });
    setActive(-1);
  }, [menu]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = () => onClose();
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); return; }
      if (!actionable.length) return;
      const at = actionable.indexOf(active);
      if (e.key === "ArrowDown") { e.preventDefault(); setActive(actionable[(at + 1) % actionable.length]); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive(actionable[(at <= 0 ? actionable.length : at) - 1]); }
      else if (e.key === "Home") { e.preventDefault(); setActive(actionable[0]); }
      else if (e.key === "End") { e.preventDefault(); setActive(actionable[actionable.length - 1]); }
      else if (e.key === "Enter" && active >= 0) { e.preventDefault(); run(menu.items[active]); }
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", close);
    window.addEventListener("blur", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("blur", close);
      window.removeEventListener("scroll", close, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, active]);

  if (!menu || !menu.items?.length) return null;
  const run = (it) => { if (!it || it.disabled || it.separator || it.heading) return; onClose(); it.onClick?.(); };
  return createPortal(
    <div ref={ref} role="menu" data-testid="context-menu" onContextMenu={e => e.preventDefault()}
      style={{ position: "fixed", left: pos.left, top: pos.top, zIndex: 3000, minWidth: 200, maxWidth: 320, padding: "4px 0", background: "var(--surface)", color: "var(--text)", border: "1px solid var(--cs-border)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.25)", fontSize: 12.5 }}>
      {menu.items.map((it, i) => {
        if (it.separator) return <div key={i} role="separator" style={{ height: 1, margin: "4px 0", background: "var(--cs-border)" }} />;
        if (it.heading) return <div key={i} style={{ padding: "4px 12px 2px", fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "var(--text3)", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.heading}</div>;
        return (
          <div key={i} role="menuitem" aria-disabled={it.disabled ? "true" : undefined} title={it.disabled ? it.why || "" : it.why || ""}
            onMouseEnter={() => !it.disabled && setActive(i)}
            onClick={() => run(it)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "5px 12px", cursor: it.disabled ? "default" : "pointer", opacity: it.disabled ? 0.45 : 1, color: it.danger && !it.disabled ? "#dc2626" : "inherit", background: active === i ? "var(--surface2)" : "transparent" }}>
            <span style={{ width: 12, textAlign: "center" }}>{it.checked ? "✓" : ""}</span>
            <span style={{ flex: 1 }}>{it.label}</span>
            {it.hint && <span style={{ fontSize: 11, color: "var(--text3)" }}>{it.hint}</span>}
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
